// ─── src/services/aiSignalService.js ─────────────────────────────────────────
// Fetches aggregated behavioral signals (clicks + enquiries) per listing from
// lwd_intelligence_events. Used to inject a real-world performance boost into
// the AI relevance scorer after intent is resolved.
//
// Score contribution (max 0.15) is intentionally capped below style match (0.40)
// and token density (0.50) so behavioral signals act as tie-breakers, not
// overrides. A listing with 50 clicks but zero style match should not rank
// above one with perfect intent alignment.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase } from "../lib/supabaseClient";

const SIGNAL_WINDOW_DAYS = 90;
const CLICK_WEIGHT       = 1.0;
const ENQUIRY_WEIGHT     = 4.0;   // enquiry is a stronger signal than a click
const MAX_BOOST          = 0.15;

// In-memory cache keyed by category, signals don't change mid-session
const _cache = {};

/**
 * Fetch behavioral signal scores per listing.
 * Returns a map of { [vendorId]: normalizedScore (0–0.15) }.
 *
 * @param {string} category  e.g. "wedding-venues"
 * @returns {Promise<Record<string, number>>}
 */
export async function fetchAISignals(category) {
  const key = category || "all";
  if (_cache[key]) return _cache[key];

  try {
    const since = new Date(
      Date.now() - SIGNAL_WINDOW_DAYS * 24 * 60 * 60 * 1000
    ).toISOString();

    const { data, error } = await supabase
      .from("lwd_intelligence_events")
      .select("vendor_id, event_type")
      .in("event_type", ["ai_result_click", "enquiry_started", "enquiry_submitted"])
      .not("vendor_id", "is", null)
      .gte("created_at", since);

    if (error || !data?.length) return {};

    // Aggregate client-side, catalog is small enough that fetching raw events
    // is cheaper than a server-side RPC for now
    const raw = {};
    for (const { vendor_id, event_type } of data) {
      if (!raw[vendor_id]) raw[vendor_id] = { clicks: 0, enquiries: 0 };
      if (event_type === "ai_result_click")                 raw[vendor_id].clicks++;
      if (event_type === "enquiry_started" ||
          event_type === "enquiry_submitted")               raw[vendor_id].enquiries++;
    }

    // Compute raw scores, then normalise to [0, MAX_BOOST]
    const rawScores = Object.entries(raw).map(([id, s]) => ({
      id,
      score: s.clicks * CLICK_WEIGHT + s.enquiries * ENQUIRY_WEIGHT,
    }));

    if (!rawScores.length) return {};

    const maxRaw = Math.max(...rawScores.map(s => s.score));
    const signals = {};
    for (const { id, score } of rawScores) {
      signals[id] = maxRaw > 0 ? (score / maxRaw) * MAX_BOOST : 0;
    }

    _cache[key] = signals;
    return signals;
  } catch {
    return {};
  }
}
